import { Alert, Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { HStack, Loader } from "@zfp-akademie-a-s/zfp-design";
import { FormRadioGroup, FormSwitch } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getGetBudgetOverviewQueryKey,
  getGetModelingAvailableAmountQueryKey,
  useGetAnalysisClients,
  useGetBudgetOverview,
  useGetConsumptionExpense,
  useGetFinancialProductExpense,
  useGetHousingExpense,
  useGetInsuranceExpense,
  useGetLifeStyleExpense,
  useGetLoanExpense,
  useGetOtherExpense,
  useListIncomes,
  useSetExpensesEntryMode,
  useSetIncomesEntryMode,
  useUpdateAggregatedExpenseCategory,
  useUpdateAggregatedIncomes,
  useUpdateAggregatedOtherExpenses,
  useUpdateAllocatedGoalAmount,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type BudgetOverviewExpensesEntryMode,
  type BudgetOverviewIncomesEntryMode,
  type ExpenseLine,
  ExpenseLineExpenseType,
} from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { AddExpenseDrawer } from "./Expenses/Components/AddExpenseDrawer";
import { ExpenseTile } from "./Expenses/Components/ExpenseTile";
import { AddIncomeDrawer } from "./Income/Components/AddIncomeDrawer";
import { IncomeBulkDrawer } from "./Income/Components/IncomeBulkDrawer";
import { IncomeTile } from "./Income/Components/IncomeTile";
import { Recap } from "./Recap/Recap";
import { parseFormNumber } from "./parseFormNumber";

type Props = {
  analysisId: string;
  versionId: string;
};

export const BudgetPane: FC<Props> = ({ analysisId, versionId }) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const isTablet = useResponsive(TABLET_MAX_WIDTH);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const isBudgetBulkEntryEnabled = useFeatureFlag(FeatureFlag.BudgetBulkEntry);

  const [incomeDrawerOpen, setIncomeDrawerOpen] = useState(false);
  // null = closed; incomeGuid = opened from that income's tile
  const [incomeBulkDrawer, setIncomeBulkDrawer] = useState<{ readonly incomeGuid?: string } | null>(null);
  const [expensesDrawerOpen, setExpensesDrawerOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const skipNextAllocatedPutRef = useRef(false);
  const suppressRecalcRef = useRef(false);
  const budgetSyncRef = useRef<{ isBudgetLoading: boolean; recalculateAllocatedGoalAmount: () => void }>({
    isBudgetLoading: true,
    recalculateAllocatedGoalAmount: () => {},
  });

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  // Orchestration layer: the old single useBudgetQuery is now assembled from the overview
  // (entry-modes + 4 aggregates + allocatedGoalAmount) plus every per-category query that the
  // individual expense/income forms already use. React Query serves these from cache when the
  // relevant drawer/form is also mounted, so this fan-out does not cause duplicate network calls.
  const { data: overview, isLoading: isOverviewLoading } = useGetBudgetOverview(analysisId, versionId);
  const { data: incomes, isLoading: isIncomesLoading } = useListIncomes(analysisId, versionId);
  const { data: housingExpense, isLoading: isHousingLoading } = useGetHousingExpense(analysisId, versionId);
  const { data: consumptionExpense, isLoading: isConsumptionLoading } = useGetConsumptionExpense(analysisId, versionId);
  const { data: lifeStyleExpense, isLoading: isLifeStyleLoading } = useGetLifeStyleExpense(analysisId, versionId);
  const { data: otherExpense, isLoading: isOtherLoading } = useGetOtherExpense(analysisId, versionId);
  const { data: loanExpense, isLoading: isLoanItemsLoading } = useGetLoanExpense(analysisId, versionId);
  const { data: financialProductExpense, isLoading: isFinancialProductItemsLoading } = useGetFinancialProductExpense(analysisId, versionId);
  const { data: insuranceExpense, isLoading: isInsuranceItemsLoading } = useGetInsuranceExpense(analysisId, versionId);

  const isBudgetLoading =
    isOverviewLoading ||
    isIncomesLoading ||
    isHousingLoading ||
    isConsumptionLoading ||
    isLifeStyleLoading ||
    isOtherLoading ||
    isLoanItemsLoading ||
    isFinancialProductItemsLoading ||
    isInsuranceItemsLoading;

  const { mutate: setIncomesEntryMode } = useSetIncomesEntryMode();
  const { mutate: setExpensesEntryMode } = useSetExpensesEntryMode();
  const { mutate: updateAggregatedIncomes } = useUpdateAggregatedIncomes();
  const { mutate: updateAggregatedExpenseCategory } = useUpdateAggregatedExpenseCategory();
  const { mutate: updateAggregatedOtherExpenses } = useUpdateAggregatedOtherExpenses();
  const { mutate: updateAllocatedGoalAmount } = useUpdateAllocatedGoalAmount();

  // Per-category individual-mode totals. All 7 categories carry their own independent entry-mode
  // (a "detailed" checkbox inside their own edit drawer, unrelated to this pane's own top-level
  // toggle) and clear their items/amounts to [] whenever that category is set to its own aggregate
  // sub-mode, while keeping `aggregatedAmount` in sync on every save regardless of sub-mode - so
  // `aggregatedAmount` is the only value that is always correct, unlike summing items/amounts[].
  const housingTotal = housingExpense?.aggregatedAmount?.value ?? 0;
  const consumptionTotal = consumptionExpense?.aggregatedAmount?.value ?? 0;
  const lifeStyleTotal = lifeStyleExpense?.aggregatedAmount?.value ?? 0;
  const otherCategoryTotal = otherExpense?.aggregatedAmount?.value ?? 0;

  const loanItemsTotal = loanExpense?.aggregatedAmount?.value ?? 0;
  const financialProductItemsTotal = financialProductExpense?.aggregatedAmount?.value ?? 0;
  const insuranceItemsTotal = insuranceExpense?.aggregatedAmount?.value ?? 0;

  const incomesTotal = (incomes ?? []).reduce(
    (acc, income) => acc + income.incomeAmounts.reduce((sum, incomeAmount) => sum + incomeAmount.amount.value, 0),
    0,
  );

  const { control, watch, setValue } = useForm({
    defaultValues: {
      incomeEntryType: overview?.incomesEntryMode ?? "aggregate",
      expensesEntryType: overview?.expensesEntryMode ?? "aggregate",
      aggregatedIncomesAmount: overview?.aggregatedIncomes?.aggregatedAmount?.value.toString() ?? "",
      insuranceExpensesAmount: overview?.aggregatedInsuranceExpenses?.aggregatedAmount?.value.toString() ?? "",
      financialProductExpensesAmount: overview?.aggregatedFinancialProductExpenses?.aggregatedAmount?.value.toString() ?? "",
      loanExpensesAmount: overview?.aggregatedLoanExpenses?.aggregatedAmount?.value.toString() ?? "",
      otherExpensesAmount: overview?.aggregatedOtherExpenses?.aggregatedAmount?.value.toString() ?? "",
      allocatedGoalAmount: overview?.allocatedGoalAmount?.allocatedGoalAmount?.value.toString() ?? "",
      isAllocatedGoalAmountManuallySet: overview?.allocatedGoalAmount?.isAllocatedGoalAmountManuallySet ?? false,
    },
  });

  const incomeEntryType = watch("incomeEntryType") as BudgetOverviewIncomesEntryMode;
  const expensesEntryType = watch("expensesEntryType") as BudgetOverviewExpensesEntryMode;
  const aggregatedIncomesAmount = watch("aggregatedIncomesAmount");
  const insuranceExpensesAmount = watch("insuranceExpensesAmount");
  const financialProductExpensesAmount = watch("financialProductExpensesAmount");
  const loanExpensesAmount = watch("loanExpensesAmount");
  const otherExpensesAmount = watch("otherExpensesAmount");
  const allocatedGoalAmount = watch("allocatedGoalAmount");
  const isAllocatedGoalAmountManuallySet = watch("isAllocatedGoalAmountManuallySet");

  const recalculateAllocatedGoalAmount = useCallback(() => {
    // The recalculation writes to the BE on its own, without any user action - on a completed or
    // declined analysis merely opening this tab would rewrite the stored value.
    if (readOnly || isAllocatedGoalAmountManuallySet || isBudgetLoading || suppressRecalcRef.current) {
      return;
    }

    const totalIncomes = incomeEntryType === "aggregate" ? parseFormNumber(aggregatedIncomesAmount) : incomesTotal;

    const totalExpenses =
      expensesEntryType === "aggregate"
        ? parseFormNumber(insuranceExpensesAmount) +
          parseFormNumber(financialProductExpensesAmount) +
          parseFormNumber(loanExpensesAmount) +
          parseFormNumber(otherExpensesAmount)
        : insuranceItemsTotal +
          financialProductItemsTotal +
          loanItemsTotal +
          housingTotal +
          consumptionTotal +
          lifeStyleTotal +
          otherCategoryTotal;

    const calculated = totalIncomes > totalExpenses ? totalIncomes - totalExpenses : 0;

    if (parseFormNumber(allocatedGoalAmount) === calculated) {
      return;
    }

    skipNextAllocatedPutRef.current = true;
    setValue("allocatedGoalAmount", calculated.toString());

    updateAllocatedGoalAmount(
      {
        analysisId,
        versionId,
        data: {
          allocatedGoalAmount: {
            value: calculated,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          isAllocatedGoalAmountManuallySet: isAllocatedGoalAmountManuallySet,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.AllocatedGoalAmount.Form.Errors.SubmitError));
        },
      },
    );
  }, [
    readOnly,
    isAllocatedGoalAmountManuallySet,
    isBudgetLoading,
    incomeEntryType,
    expensesEntryType,
    aggregatedIncomesAmount,
    insuranceExpensesAmount,
    financialProductExpensesAmount,
    loanExpensesAmount,
    otherExpensesAmount,
    incomesTotal,
    insuranceItemsTotal,
    financialProductItemsTotal,
    loanItemsTotal,
    housingTotal,
    consumptionTotal,
    lifeStyleTotal,
    otherCategoryTotal,
    allocatedGoalAmount,
    setValue,
    updateAllocatedGoalAmount,
    analysisId,
    versionId,
    queryClient,
    t,
  ]);

  useEffect(() => {
    if (!overview || isBudgetLoading) {
      return;
    }
    if (suppressRecalcRef.current) {
      return;
    }
    recalculateAllocatedGoalAmount();
  }, [overview, isBudgetLoading, recalculateAllocatedGoalAmount]);

  // Latest values read only from a deferred callback below — kept fresh via ref so the sync
  // effect's deps can stay limited to [overview, setValue] without going stale.
  useEffect(() => {
    budgetSyncRef.current = { isBudgetLoading, recalculateAllocatedGoalAmount };
  }, [isBudgetLoading, recalculateAllocatedGoalAmount]);

  useEffect(() => {
    if (!overview) {
      return;
    }

    suppressRecalcRef.current = true;

    setValue("incomeEntryType", overview.incomesEntryMode ?? "aggregate");
    if (overview.incomesEntryMode === "aggregate" && overview.aggregatedIncomes?.aggregatedAmount?.value !== undefined) {
      setValue("aggregatedIncomesAmount", overview.aggregatedIncomes.aggregatedAmount.value.toString());
    }

    setValue("expensesEntryType", overview.expensesEntryMode ?? "aggregate");
    if (overview.expensesEntryMode === "aggregate") {
      setValue("insuranceExpensesAmount", (overview.aggregatedInsuranceExpenses?.aggregatedAmount?.value.toString() as any) ?? "");
      setValue(
        "financialProductExpensesAmount",
        (overview.aggregatedFinancialProductExpenses?.aggregatedAmount?.value.toString() as any) ?? "",
      );
      setValue("loanExpensesAmount", (overview.aggregatedLoanExpenses?.aggregatedAmount?.value.toString() as any) ?? "");
      setValue("otherExpensesAmount", (overview.aggregatedOtherExpenses?.aggregatedAmount?.value.toString() as any) ?? "");
    }

    const isManuallySet = overview.allocatedGoalAmount?.isAllocatedGoalAmountManuallySet ?? false;
    setValue("allocatedGoalAmount", overview.allocatedGoalAmount?.allocatedGoalAmount?.value.toString() ?? "");
    setValue("isAllocatedGoalAmountManuallySet", isManuallySet);
    skipNextAllocatedPutRef.current = true;

    setIsFirstLoad(false);

    queueMicrotask(() => {
      suppressRecalcRef.current = false;
      if (!budgetSyncRef.current.isBudgetLoading && !isManuallySet) {
        budgetSyncRef.current.recalculateAllocatedGoalAmount();
      }
    });
  }, [overview, setValue]);

  const handleIncomesEntryTypeChange = useCallback(
    (value: string) => {
      setValue("incomeEntryType", value as BudgetOverviewIncomesEntryMode);

      setIncomesEntryMode(
        {
          analysisId,
          versionId,
          mode: value as "aggregate" | "individual",
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    },
    [analysisId, versionId, setIncomesEntryMode, queryClient, setValue, t],
  );

  const handleExpensesEntryTypeChange = useCallback(
    (value: string) => {
      setValue("expensesEntryType", value as BudgetOverviewExpensesEntryMode);

      setExpensesEntryMode(
        {
          analysisId,
          versionId,
          mode: value as "aggregate" | "individual",
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
          },
        },
      );
    },
    [analysisId, versionId, setExpensesEntryMode, queryClient, setValue, t],
  );

  const handleSaveAggregatedIncomes = () => {
    if (incomeEntryType === "aggregate") {
      updateAggregatedIncomes(
        {
          analysisId,
          versionId,
          data: {
            aggregatedAmount: {
              value: parseFormNumber(aggregatedIncomesAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.Incomes.Form.Errors.IncomeSubmitError));
          },
        },
      );
    }
  };

  const handleSaveAggregatedExpenses = () => {
    if (expensesEntryType !== "aggregate") {
      return;
    }

    const buildAmount = (raw: string | undefined) => ({
      value: parseFormNumber(raw),
      currencyCode: "CZK",
      frequency: AmountFrequency.monthly,
    });

    const onSuccess = () => {
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    };
    const onError = () => {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    };

    updateAggregatedExpenseCategory(
      { analysisId, versionId, category: "insurance", data: { aggregatedAmount: buildAmount(insuranceExpensesAmount) } },
      { onSuccess, onError },
    );
    updateAggregatedExpenseCategory(
      { analysisId, versionId, category: "financial-product", data: { aggregatedAmount: buildAmount(financialProductExpensesAmount) } },
      { onSuccess, onError },
    );
    updateAggregatedExpenseCategory(
      { analysisId, versionId, category: "loan", data: { aggregatedAmount: buildAmount(loanExpensesAmount) } },
      { onSuccess, onError },
    );
    updateAggregatedOtherExpenses(
      { analysisId, versionId, data: { aggregatedAmount: buildAmount(otherExpensesAmount) } },
      { onSuccess, onError },
    );
  };

  useEffect(() => {
    if (readOnly || isFirstLoad) {
      return;
    }

    if (skipNextAllocatedPutRef.current) {
      skipNextAllocatedPutRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      updateAllocatedGoalAmount(
        {
          analysisId,
          versionId,
          data: {
            allocatedGoalAmount: {
              value: parseFormNumber(allocatedGoalAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            isAllocatedGoalAmountManuallySet: isAllocatedGoalAmountManuallySet,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Budget.AllocatedGoalAmount.Form.Errors.SubmitError));
          },
        },
      );
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    readOnly,
    isFirstLoad,
    allocatedGoalAmount,
    isAllocatedGoalAmountManuallySet,
    analysisId,
    versionId,
    updateAllocatedGoalAmount,
    queryClient,
    t,
  ]);

  if (isFirstLoad && isBudgetLoading) {
    return <Loader />;
  }

  // One synthetic ExpenseLine per category, built from the per-category totals above — the tiles
  // render an aggregate, not the individual expense lines the API returns.
  const monthlyAmount = (value: number) => ({ value, currencyCode: CurrencyCode.CZK, frequency: AmountFrequency.monthly });

  const expenseCategoryItems: ExpenseLine[] = [
    { expenseType: ExpenseLineExpenseType.housing, amount: monthlyAmount(housingTotal) },
    { expenseType: ExpenseLineExpenseType.consumption, amount: monthlyAmount(consumptionTotal) },
    { expenseType: ExpenseLineExpenseType.life_style, amount: monthlyAmount(lifeStyleTotal) },
    { expenseType: ExpenseLineExpenseType.loan, amount: monthlyAmount(loanItemsTotal) },
    { expenseType: ExpenseLineExpenseType.insurance, amount: monthlyAmount(insuranceItemsTotal) },
    { expenseType: ExpenseLineExpenseType["financial-product"], amount: monthlyAmount(financialProductItemsTotal) },
    { expenseType: ExpenseLineExpenseType.other, amount: monthlyAmount(otherCategoryTotal) },
  ];

  const isFinancialProductVisible =
    (financialProductExpense?.entryMode === "individual" && (financialProductExpense?.items?.length ?? 0) > 0) ||
    (financialProductExpense?.entryMode === "aggregate" && (financialProductExpense?.aggregatedAmount?.value ?? 0) > 0);

  const visibleExpenseItems = expenseCategoryItems.filter((expense) =>
    expense.expenseType === ExpenseLineExpenseType["financial-product"] ? isFinancialProductVisible : expense.amount.value > 0,
  );

  return (
    <Box sx={{ marginBottom: "120px !important" }}>
      <Stack direction={isTablet ? "column" : "row"}>
        <Stack spacing={Spacing.XXXL} flex={4}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography variant="h3">{t(Resources.AnalysisDetail.TabMenu.Budget)}</Typography>
              <Typography variant="body2" color="secondary">
                {t(Resources.AnalysisDetail.Budget.Subtitle)}
              </Typography>
            </Box>
          </Box>

          <Stack width={640} spacing={Spacing.XXL} component="form">
            <Stack spacing={Spacing.XL}>
              <Typography variant="subtitle1" fontWeight={600} mb={Spacing.XL}>
                {t(Resources.AnalysisDetail.Budget.Incomes.Title)}
              </Typography>
              <Box>
                <FormRadioGroup
                  control={control}
                  name="incomeEntryType"
                  label={t(Resources.AnalysisDetail.Budget.EntryType)}
                  options={toRadioOptions([
                    {
                      code: "aggregate",
                      name: t(Resources.AnalysisDetail.Budget.Aggregated),
                    },
                    {
                      code: "individual",
                      name: t(Resources.AnalysisDetail.Budget.Individual),
                    },
                  ])}
                  defaultValue="aggregate"
                  columns={2}
                  disabled={readOnly}
                  onChange={(value) => handleIncomesEntryTypeChange(value)}
                />
                {incomeEntryType === "individual" ? (
                  <Box>
                    {incomes && incomes.length > 0 ? (
                      <Grid mb={Spacing.L} container spacing={Spacing.L}>
                        {incomes.map((income) => (
                          <Grid key={income.guid} size={6}>
                            <IncomeTile
                              income={income}
                              clients={clientsData?.clients ?? []}
                              onEdit={isBudgetBulkEntryEnabled ? () => setIncomeBulkDrawer({ incomeGuid: income.guid }) : undefined}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : null}

                    <HStack spacing={Spacing.M}>
                      <Button
                        variant="text"
                        disabled={readOnly}
                        sx={{ width: "fit-content", margin: 0 }}
                        startIcon={<AddIcon />}
                        onClick={() => setIncomeDrawerOpen(true)}
                      >
                        {t(Resources.AnalysisDetail.Budget.Incomes.Add)}
                      </Button>

                      {isBudgetBulkEntryEnabled && (
                        <Button
                          variant="text"
                          disabled={readOnly}
                          sx={{ width: "fit-content", margin: 0 }}
                          startIcon={<AddIcon />}
                          onClick={() => setIncomeBulkDrawer({})}
                        >
                          {t(Resources.AnalysisDetail.Budget.Incomes.AddMultiple)}
                        </Button>
                      )}
                    </HStack>
                  </Box>
                ) : (
                  <Grid container spacing={Spacing.L} mt={Spacing.L}>
                    <Grid size={6}>
                      <FormNumberField
                        control={control}
                        name="aggregatedIncomesAmount"
                        label={t(Resources.AnalysisDetail.Budget.Incomes.MonthlyIncomes)}
                        required
                        valueType="text"
                        scale={2}
                        min={0}
                        thousandsSeparator=" "
                        radix=","
                        mapToRadix={["."]}
                        unit={t(Resources.Common.CZKSuffix)}
                        disabled={readOnly}
                        placeholder={t(Resources.AnalysisDetail.Budget.Incomes.AggregatedIncomesAmount)}
                        onBlur={() => handleSaveAggregatedIncomes()}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack spacing={Spacing.XL}>
              <Typography variant="subtitle1" fontWeight={600} mb={Spacing.XL}>
                {t(Resources.AnalysisDetail.Budget.Expenses.Title)}
              </Typography>

              <FormRadioGroup
                control={control}
                name="expensesEntryType"
                label={t(Resources.AnalysisDetail.Budget.EntryType)}
                options={toRadioOptions([
                  {
                    code: "aggregate",
                    name: t(Resources.AnalysisDetail.Budget.Aggregated),
                  },
                  {
                    code: "individual",
                    name: t(Resources.AnalysisDetail.Budget.Individual),
                  },
                ])}
                defaultValue="aggregate"
                columns={2}
                disabled={readOnly}
                onChange={(value) => handleExpensesEntryTypeChange(value)}
              />

              <Box>
                {expensesEntryType === "individual" ? (
                  <>
                    {visibleExpenseItems.length > 0 ? (
                      <Grid container mb={Spacing.L} spacing={Spacing.L}>
                        {visibleExpenseItems.map((expense) => (
                          <Grid key={expense.expenseType} size={6}>
                            <ExpenseTile expense={expense} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : null}

                    <Button
                      variant="text"
                      disabled={readOnly}
                      sx={{ width: "fit-content", margin: 0 }}
                      startIcon={<AddIcon />}
                      onClick={() => setExpensesDrawerOpen(true)}
                    >
                      {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.Add)}
                    </Button>
                  </>
                ) : (
                  <>
                    <Grid container spacing={Spacing.L}>
                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="insuranceExpensesAmount"
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.InsuranceExpensesAmount)}
                          required
                          valueType="text"
                          scale={2}
                          min={0}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          placeholder="0"
                          onBlur={() => handleSaveAggregatedExpenses()}
                          fullWidth
                        />
                      </Grid>

                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="financialProductExpensesAmount"
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.FinancialProductExpensesAmount)}
                          required
                          valueType="text"
                          scale={2}
                          min={0}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          placeholder="0"
                          onBlur={() => handleSaveAggregatedExpenses()}
                          fullWidth
                        />
                      </Grid>

                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="loanExpensesAmount"
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.LoanExpensesAmount)}
                          required
                          valueType="text"
                          scale={2}
                          min={0}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          placeholder="0"
                          onBlur={() => handleSaveAggregatedExpenses()}
                          fullWidth
                        />
                      </Grid>

                      <Grid size={6}>
                        <FormNumberField
                          control={control}
                          name="otherExpensesAmount"
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.OtherExpensesAmount)}
                          required
                          valueType="text"
                          scale={2}
                          min={0}
                          thousandsSeparator=" "
                          radix=","
                          mapToRadix={["."]}
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          placeholder="0"
                          onBlur={() => handleSaveAggregatedExpenses()}
                          fullWidth
                        />
                      </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: Spacing.XL }}>
                      {t(Resources.AnalysisDetail.Budget.Expenses.Hint)}
                    </Alert>
                  </>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack spacing={Spacing.S} mb={Spacing.XL}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t(Resources.AnalysisDetail.Budget.Recap.Difference)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.AnalysisDetail.Budget.Recap.DifferenceTooltip)}
              </Typography>
            </Stack>

            <Grid container spacing={Spacing.L} mb={isTablet ? 0 : Spacing.MAX}>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="allocatedGoalAmount"
                  label={t(Resources.AnalysisDetail.Budget.AllocatedGoalAmount.Amount)}
                  info={t(Resources.AnalysisDetail.Budget.AllocatedGoalAmount.AmountTooltip) as string}
                  infoLabel={t(Resources.Common.MoreInfo)}
                  required
                  valueType="text"
                  scale={2}
                  min={0}
                  thousandsSeparator=" "
                  radix=","
                  mapToRadix={["."]}
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly || !isAllocatedGoalAmountManuallySet}
                  placeholder="0"
                  fullWidth
                />
              </Grid>

              <Grid size={6} sx={{ margin: "auto 0 2px 0" }}>
                <FormSwitch
                  control={control}
                  name="isAllocatedGoalAmountManuallySet"
                  label={t(Resources.AnalysisDetail.Budget.AllocatedGoalAmount.Form.Fields.IsAllocatedGoalAmountManuallySet)}
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
        {/* !important here because of parent spacing */}
        <Divider
          orientation={isTablet ? "horizontal" : "vertical"}
          flexItem
          sx={{ margin: isTablet ? "48px 0 0 0 !important" : "-24px 0 0 0 !important" }}
        />
        <Stack sx={{ flex: 2 }}>
          <Recap
            overview={overview}
            incomesTotal={incomesTotal}
            housingTotal={housingTotal}
            consumptionTotal={consumptionTotal}
            lifeStyleTotal={lifeStyleTotal}
            otherCategoryTotal={otherCategoryTotal}
            insuranceItemsTotal={insuranceItemsTotal}
            financialProductItemsTotal={financialProductItemsTotal}
            loanItemsTotal={loanItemsTotal}
            allocatedGoalAmount={parseFormNumber(allocatedGoalAmount)}
            isTablet={isTablet}
            incomesEntryMode={incomeEntryType}
            expensesEntryMode={expensesEntryType}
            liveIncomesAmount={incomeEntryType === "aggregate" ? parseFormNumber(aggregatedIncomesAmount) : undefined}
            liveInsuranceExpenses={expensesEntryType === "aggregate" ? parseFormNumber(insuranceExpensesAmount) : undefined}
            liveFinancialProductExpenses={expensesEntryType === "aggregate" ? parseFormNumber(financialProductExpensesAmount) : undefined}
            liveLoanExpenses={expensesEntryType === "aggregate" ? parseFormNumber(loanExpensesAmount) : undefined}
            liveOtherExpenses={expensesEntryType === "aggregate" ? parseFormNumber(otherExpensesAmount) : undefined}
          />
        </Stack>
      </Stack>

      <AddIncomeDrawer open={incomeDrawerOpen} onClose={() => setIncomeDrawerOpen(false)} clients={clientsData?.clients} />

      {isBudgetBulkEntryEnabled && (
        <IncomeBulkDrawer
          open={!!incomeBulkDrawer}
          initialIncomeGuid={incomeBulkDrawer?.incomeGuid}
          onClose={() => setIncomeBulkDrawer(null)}
        />
      )}

      <AddExpenseDrawer open={expensesDrawerOpen} onClose={() => setExpensesDrawerOpen(false)} clients={clientsData?.clients} />
    </Box>
  );
};
